import { has } from 'lodash';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { Block } from '@/components/block';
import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { LocationAutocompletePair } from '@/components/location-autocomplete-pair';
import { Text } from '@/components/text';
import { AdvancedDatePicker } from '@/components/ui/inputs/advanced-date-picker';
import { DateRangeValue } from '@/components/ui/inputs/advanced-date-picker/types';
import { dateRangeToSimpleDates } from '@/components/ui/inputs/advanced-date-picker/utils';
import { AIRPORT, MODE_AIR, MODE_WATER, SCHEDULE_SEARCH_URL, SEAPORT } from '@/lib/constants';
import { DEFAULT_DATE_RANGE } from '@/state/standalone-schedules';
import { PlaceInterface } from '@/types/api-types';
import { ScheduleSearchBlockProps } from '@/types/block-types';
import { getCarrierName } from '@/utils/carrier';
import { formatDateTimeForUrl } from '@/utils/format/date-time';
import { getModePlaceholderText } from '@/utils/format/mode';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';
import { getLocationUrlQueryValue } from '@/utils/places';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

const ScheduleSearchBlock = ({ id, isUnderPageHeader, mode, searchProps }: ScheduleSearchBlockProps) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [origin, setOrigin] = useState<PlaceInterface | undefined>();
  const [destination, setDestination] = useState<PlaceInterface | undefined>();
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    type: DEFAULT_DATE_RANGE.type,
    date: searchProps?.departDate || DEFAULT_DATE_RANGE.date,
  });

  const parsedDates = dateRangeToSimpleDates(dateRange);
  const searchModeType = mode === MODE_AIR ? AIRPORT : SEAPORT;

  const handleChangeLocations = (value: { origin?: PlaceInterface; destination?: PlaceInterface }) => {
    if (has(value, 'origin')) {
      setOrigin(value.origin);
    }
    if (has(value, 'destination')) {
      setDestination(value.destination);
    }
  };

  const handleSearchClick = () => {
    if (origin && destination && mode) {
      setIsLoading(true);

      const query = {
        [QUERY_PARAM.ORIGIN]: getLocationUrlQueryValue(origin),
        [QUERY_PARAM.DESTINATION]: getLocationUrlQueryValue(destination),
        [QUERY_PARAM.MODE]: mode,
        [QUERY_PARAM.DATE_FROM]: parsedDates.dateFrom ? formatDateTimeForUrl(parsedDates.dateFrom) : '',
        [QUERY_PARAM.DATE_TO]: parsedDates.dateTo ? formatDateTimeForUrl(parsedDates.dateTo) : '',
        [QUERY_PARAM.FILTERS]: JSON.stringify({ carrierIds: [searchProps?.carrierId] }),
      };

      router.push({ pathname: SCHEDULE_SEARCH_URL, query }, undefined, {
        shallow: true,
      });

      trackUser.event('Carrier Schedules: Searched', {
        carrierId: searchProps?.carrierId,
      });
    }
  };

  const { base, wrapper, header, searchButton, formRow } = styles({ isUnderPageHeader });

  return (
    <Block id={id} className={base()}>
      <div className={wrapper()}>
        <div className={header()}>
          <Heading variant="h2" className="-mt-xs mb-xs">
            Explore more schedules
          </Heading>
          <Text variant="secondary">
            Enter an origin and destination, select a date and press search see more{' '}
            {searchProps?.carrier ? getCarrierName(searchProps.carrier) : ''} {mode === MODE_WATER ? 'sailing ' : ''}
            schedules
          </Text>
        </div>
        <div className={formRow()}>
          <LocationAutocompletePair
            placeholderText={getModePlaceholderText(mode || MODE_AIR)}
            initialOrigin={getLocationUrlQueryValue(searchProps?.origin)}
            initialDestination={getLocationUrlQueryValue(searchProps?.destination)}
            origin={origin}
            destination={destination}
            onChange={handleChangeLocations}
            placeTypes={[searchModeType]}
            colorVariant="dark"
          />
          <AdvancedDatePicker
            onChange={setDateRange}
            value={dateRange}
            onSearch={handleSearchClick}
            onReset={() => setDateRange({ type: DEFAULT_DATE_RANGE.type, date: DEFAULT_DATE_RANGE.date })}
          />

          <Button
            iconLeft="search"
            isLoading={isLoading}
            disabled={isLoading}
            onClick={handleSearchClick}
            className={searchButton()}
            data-cy="schedule-search-button"
          >
            See schedules
          </Button>
        </div>
      </div>
    </Block>
  );
};

const styles = tv({
  slots: {
    base: 'relative',
    wrapper: '-mx-3 rounded-lg bg-brandDarkBlue p-6 md:p-10 lg:-mx-2',
    header: 'text-white',
    formRow: 'flex flex-col gap-4 pt-3 md:flex-row md:items-end md:gap-2',
    searchButton:
      'z-2 h-[var(--input-height)] pl-4 pr-4 text-baseSm font-normal tracking-[0.02rem] sm:pl-4 sm:pr-4 md:ml-1',
    footer: 'mt-4 text-white',
  },
  variants: {
    isUnderPageHeader: {
      true: {
        base: '-mt-6 py-0',
      },
    },
  },
});

export { ScheduleSearchBlock };
